import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, User, Briefcase } from "lucide-react";
import PolicySignDialog from "@/components/PolicySignDialog";

// Konstante za samostojne podjetnike (SP)
const SP_OSKRBA = 35;
const SP_PRISPEVKI = 650;
const SP_PRISPEVKI_HALF = SP_PRISPEVKI / 2; // 325 €

// Nadomestilo, ki ga SP dejansko prejme (po polovičnih prispevkih v 2. mesecu)
function spNadomestiloPerMonth(month) {
  if (month === 1) return 0;
  if (month === 2) return SP_PRISPEVKI_HALF; // 325 €
  return SP_PRISPEVKI; // 650 €
}

const fmt = (n) =>
  new Intl.NumberFormat("sl-SI", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

export default function SickLeaveCalculator() {
  const [mode, setMode] = useState("employee"); // "employee" | "sp"
  const [months, setMonths] = useState(3);

  // Employee inputs
  const [empSalary, setEmpSalary] = useState(1500);
  const [empTransport, setEmpTransport] = useState(120);
  const [empMeal, setEmpMeal] = useState(160);

  // SP inputs
  const [spSalary, setSpSalary] = useState(2500);
  const [spTax, setSpTax] = useState(300);

  const empResult = useMemo(() => {
    const osnova = Math.max(0, empSalary - empTransport - empMeal);
    const bolovanjeMesecno = 0.8 * osnova;
    const razlikaMesecno = empSalary - bolovanjeMesecno;
    const izguba = Math.max(0, razlikaMesecno * months);
    const nadomestiloSkupaj = bolovanjeMesecno * months;
    return { osnova, bolovanjeMesecno, razlikaMesecno, izguba, nadomestiloSkupaj };
  }, [empSalary, empTransport, empMeal, months]);

  const spResult = useMemo(() => {
    let nadomestiloSkupaj = 0;
    for (let m = 1; m <= months; m++) nadomestiloSkupaj += spNadomestiloPerMonth(m);
    const izguba = Math.max(0, spSalary * months - nadomestiloSkupaj);
    return { nadomestiloSkupaj, izguba };
  }, [spSalary, months]);

  const isEmp = mode === "employee";
  const result = isEmp ? empResult : spResult;

  const summary = isEmp
    ? {
        "Tip": "Zaposleni",
        "Mesečno nakazilo": fmt(empSalary),
        "Potni stroški": fmt(empTransport),
        "Malica": fmt(empMeal),
        "Meseci bolovanja": `${months}`,
        "Mesečno nadomestilo (80 % osnove)": fmt(empResult.bolovanjeMesecno),
        "Skupna izguba": fmt(empResult.izguba),
      }
    : {
        "Tip": "Normirani s.p.",
        "Običajno mesečno nakazilo": fmt(spSalary),
        "Akontacija dohodnine": fmt(spTax),
        "Meseci bolovanja": `${months}`,
        "Skupno nadomestilo med bolovanjem": fmt(spResult.nadomestiloSkupaj),
        "Skupna izguba": fmt(spResult.izguba),
      };

  return (
    <div data-testid="sick-leave-calculator" className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3 space-y-7">
        <div>
          <Label className="text-sm font-semibold">Vaš status</Label>
          <div className="mt-3 grid grid-cols-2 gap-2.5" data-testid="sick-mode">
            <ModeCard
              icon={<User size={18} />}
              title="Zaposleni"
              desc="Prejemate plačo z bolniško"
              active={isEmp}
              onClick={() => setMode("employee")}
              testid="sick-mode-employee"
            />
            <ModeCard
              icon={<Briefcase size={18} />}
              title="Normirani s.p."
              desc="Plačujete prispevke sami"
              active={!isEmp}
              onClick={() => setMode("sp")}
              testid="sick-mode-sp"
            />
          </div>
        </div>

        {isEmp ? (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldNum
                label="Mesečno nakazilo (€)"
                value={empSalary}
                onChange={setEmpSalary}
                testid="sick-emp-salary"
              />
              <FieldNum
                label="Meseci bolovanja"
                value={months}
                onChange={setMonths}
                min={1}
                max={24}
                testid="sick-months"
              />
              <FieldNum
                label="Potni stroški (€)"
                value={empTransport}
                onChange={setEmpTransport}
                testid="sick-emp-transport"
              />
              <FieldNum
                label="Malica (€)"
                value={empMeal}
                onChange={setEmpMeal}
                testid="sick-emp-meal"
              />
            </div>

            <div className="rounded-xl border border-[var(--vp-border)] bg-white p-5 space-y-2.5 text-sm">
              <Row label="Osnova (nakazilo − potni − malica)" value={fmt(empResult.osnova)} />
              <Row label="Mesečno nadomestilo (80 % osnove)" value={fmt(empResult.bolovanjeMesecno)} highlight />
              <Row label="Razlika na mesec" value={fmt(empResult.razlikaMesecno)} />
            </div>
          </>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldNum
                label="Mesečno nakazilo (€)"
                value={spSalary}
                onChange={setSpSalary}
                testid="sick-sp-salary"
              />
              <FieldNum
                label="Akontacija dohodnine (€)"
                value={spTax}
                onChange={setSpTax}
                testid="sick-sp-tax"
              />
              <FieldNum
                label="Meseci bolovanja"
                value={months}
                onChange={setMonths}
                min={1}
                max={24}
                testid="sick-months"
              />
            </div>

            <div className="rounded-xl border border-[var(--vp-border)] bg-white p-5 space-y-3 text-sm">
              <div className="text-xs uppercase tracking-wider text-[var(--vp-muted)] font-semibold">
                Nadomestilo iz državne blagajne — po mesecih
              </div>
              <Row label="1. mesec" value={fmt(0)} extra="ZZZS še ne izplača; sami plačate prispevke 650 €, dolgotrajno oskrbo 35 € in akontacijo" />
              <Row label="2. mesec" value={fmt(SP_PRISPEVKI_HALF)} extra="ZZZS izplača 650 €, polovica gre v prispevke; plačate še 35 € + akontacijo" highlight />
              <Row label="3. mesec naprej" value={fmt(SP_PRISPEVKI)} extra="ZZZS izplača 650 €; plačate še 35 € + akontacijo" />
              <div className="pt-2 border-t border-[var(--vp-border)]">
                <Row
                  label={`Skupno nadomestilo (${months} mes.)`}
                  value={fmt(spResult.nadomestiloSkupaj)}
                  highlight
                />
              </div>
              <div className="text-xs text-[var(--vp-muted)] flex items-start gap-1.5 pt-1">
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                Akontacija dohodnine ({fmt(spTax)}) in dolgotrajna oskrba (35 €) so vaši
                redni stroški, ki jih plačujete vsak mesec ne glede na bolovanje.
              </div>
            </div>
          </>
        )}

        <p className="text-xs text-[var(--vp-muted)] leading-relaxed">
          Podatki so <strong>informativne narave</strong> in so lahko zaradi napačnega
          vnosa ali sprememb zakonodaje drugačni. Gre za okvirni izračun.
        </p>
      </div>

      <div className="lg:col-span-2">
        <div
          className="rounded-2xl p-7 h-full flex flex-col"
          style={{ background: "var(--vp-primary)", color: "white" }}
        >
          <div className="text-sm opacity-80">Vaša izguba zaradi bolovanja</div>
          <div
            data-testid="sick-loss"
            className="font-display font-bold text-4xl lg:text-5xl mt-2 tracking-tight"
          >
            {fmt(result.izguba)}
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <ResRow
              label={`Nadomestilo skupaj (${months} mes.)`}
              value={fmt(isEmp ? empResult.nadomestiloSkupaj : spResult.nadomestiloSkupaj)}
            />
            {isEmp && (
              <>
                <ResRow label="Običajno nakazilo / mesec" value={fmt(empSalary)} />
                <ResRow label="Nadomestilo / mesec" value={fmt(empResult.bolovanjeMesecno)} />
              </>
            )}
            {!isEmp && (
              <>
                <ResRow label="Običajno nakazilo / mesec" value={fmt(spSalary)} />
                <div
                  data-testid="sick-sp-formula"
                  className="rounded-lg bg-white/10 border border-white/20 p-3 text-xs leading-relaxed mt-2"
                >
                  <div className="font-semibold mb-1.5 opacity-90">Izračun:</div>
                  <div className="opacity-90">
                    {fmt(spSalary)} × {months} mes. = {fmt(spSalary * months)}
                  </div>
                  <div className="opacity-80 mt-0.5">
                    − 0 € (1. mes.) − 325 € (2. mes.)
                    {months >= 3 && ` − 650 € × ${months - 2} (3. mes. naprej)`}
                  </div>
                  <div className="font-semibold mt-1.5">
                    = {fmt(spResult.izguba)} izgube
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-auto pt-7">
            <PolicySignDialog
              summary={summary}
              triggerLabel="Skleni si zavarovanje"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldNum({ label, value, onChange, testid, min = 0, max }) {
  return (
    <div>
      <Label className="text-sm font-semibold">{label}</Label>
      <Input
        data-testid={testid}
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value) || 0;
          if (max != null) onChange(Math.max(min, Math.min(max, v)));
          else onChange(Math.max(min, v));
        }}
        className="mt-1.5"
      />
    </div>
  );
}

function ModeCard({ icon, title, desc, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border px-4 py-3 transition-colors ${
        active
          ? "border-[var(--vp-primary)] bg-[var(--vp-primary)]/5"
          : "border-[var(--vp-border)] bg-white hover:border-[var(--vp-primary)]/40"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center"
          style={{
            background: active ? "var(--vp-primary)" : "var(--vp-sand)",
            color: active ? "white" : "var(--vp-primary)",
          }}
        >
          {icon}
        </div>
        <div>
          <div className="font-semibold text-sm">{title}</div>
          <div className="text-xs text-[var(--vp-muted)]">{desc}</div>
        </div>
      </div>
    </button>
  );
}

function Row({ label, value, extra, highlight }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <span className={highlight ? "font-semibold" : "text-[var(--vp-muted)]"}>
          {label}
        </span>
        <span
          className={`font-semibold whitespace-nowrap ${
            highlight ? "text-[var(--vp-primary)]" : ""
          }`}
        >
          {value}
        </span>
      </div>
      {extra && (
        <div className="text-xs text-[var(--vp-muted)] mt-0.5">{extra}</div>
      )}
    </div>
  );
}

function ResRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/15 pb-2">
      <span className="opacity-85">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
